import _get from 'lodash/get'

export type GroupableElement = {
  type: string
  name?: string
  outputs?: readonly string[]
  fields?: readonly { name: string; isOptional?: boolean }[]
}

const TEXT_INPUT_TYPES = new Set(['input', 'date', 'phoneNumber', 'addressAutocomplete'])

const isTextInput = (element: GroupableElement) => TEXT_INPUT_TYPES.has(element.type)

/** Checkboxes and custom confirmations only count as answered once ticked. */
export const needsConfirmation = (element: GroupableElement) =>
  element.type === 'checkbox' || element.type === 'customComponent'

/** A component that reports `<name>Missing` is answered once that flag turns false. */
const isMissingFlag = (name: string) => name.endsWith('Missing')

export const getQuestionKeys = (element: GroupableElement): readonly string[] => {
  if (element.type === 'customComponent') {
    const outputs = element.outputs ?? []
    const missingFlags = outputs.filter(isMissingFlag)
    return missingFlags.length > 0 ? missingFlags : outputs
  }
  if (element.type === 'checkbox') {
    return (element.fields ?? []).filter((field) => !field.isOptional).map((field) => field.name)
  }
  return element.name ? [element.name] : []
}

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== ''

type QuestionFormState = {
  values: Record<string, unknown>
  errors: object
  touchedFields: object
  dirtyFields: object
}

export const isQuestionAnswered = (question: GroupableElement, form: QuestionFormState) =>
  getQuestionKeys(question).every((name) => {
    if (needsConfirmation(question)) {
      return form.values[name] === !isMissingFlag(name)
    }
    const isSettled =
      !isTextInput(question) ||
      Boolean(_get(form.touchedFields, name)) ||
      !_get(form.dirtyFields, name)
    return hasValue(form.values[name]) && !_get(form.errors, name) && isSettled
  })

const isPageTitle = (element: GroupableElement, index: number) =>
  element.type === 'harmonyTitle' || (index === 0 && element.type === 'title')

/** Group index per element: each question plus the content leading up to it; -1 for content that never dims. */
export const getQuestionGroups = (elements: readonly GroupableElement[]) => {
  const questionPositions = elements.flatMap((element, index) =>
    getQuestionKeys(element).length > 0 ? [index] : []
  )
  return elements.map((element, index) => {
    if (
      isPageTitle(element, index) ||
      (element.type === 'customComponent' && !element.outputs?.length)
    ) {
      return -1
    }
    return questionPositions.findIndex((position) => position >= index)
  })
}
